import { useSyncExternalStore } from 'react';
import type { UpgradeTier } from '@/lib/upgrade-link';

/**
 * Why an upgrade prompt opened. Each reason has its own headline, benefits and
 * suggested plan in the prompt dialog, so the copy speaks to what the user just
 * tried to do instead of a generic "Upgrade".
 */
export type UpgradeReason =
  | 'sequences'
  | 'inbox'
  | 'tasks'
  | 'integrations'
  | 'agents'
  | 'credits'
  | 'search'
  | 'seats'
  | 'general';

export interface UpgradePromptRequest {
  reason: UpgradeReason;
  /** The plan to point at; defaults to the reason's own suggestion. */
  plan?: UpgradeTier;
  /** Analytics: the surface that opened it (`sidebar_locked`, `nudge`...).
   *  The nudge's (`NUDGE_SOURCE`) also starts focus on Not now. */
  source: string;
}

type Listener = () => void;

let current: UpgradePromptRequest | null = null;
const listeners = new Set<Listener>();

const emit = () => listeners.forEach((listener) => listener());

/**
 * Opens the upgrade prompt from anywhere: a click handler, a hook, an effect.
 * The dialog host mounted in the app shell renders it.
 */
export function openUpgradePrompt(request: UpgradePromptRequest): void {
  current = request;
  emit();
}

export function closeUpgradePrompt(): void {
  if (current === null) return;
  current = null;
  emit();
}

const subscribe = (listener: Listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** The open request, or null. For the dialog host. */
export function useUpgradePromptRequest(): UpgradePromptRequest | null {
  return useSyncExternalStore(
    subscribe,
    () => current,
    () => null
  );
}
